import { useCallback, useEffect, useRef, useState } from 'react';
import { loadReplay, type LoadDeps, type LoadedReplay } from './loader';
import {
  WorkReplayError,
  fetchWorkReplay,
  type FetchWorkReplay,
} from './service';
import type { WorkReplayView } from './types';

/** What the section knows about the candidate's replay, before anything is played. */
export type ReplaySummary =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'masked' }
  | { status: 'saving' }
  | { status: 'unavailable' }
  | { status: 'ready'; view: WorkReplayView };

export type StartAt = 'start' | 'end';

/** The recording itself, fetched only when the recruiter chooses to watch. */
export type ReplayPlayback =
  | { status: 'idle' }
  | { status: 'loading'; done: number; total: number; startAt: StartAt }
  | { status: 'ready'; loaded: LoadedReplay; startAt: StartAt }
  | { status: 'failed' };

/**
 * The signed links live five minutes (avrio-rio docs/work-assets.md). A view
 * older than this is asked for again before its links are used.
 */
export const LINK_FRESH_MS = 4 * 60_000;

export interface UseWorkReplayOptions {
  jobId: number;
  candidateId: number;
  /** The anonymised view: the backend refuses it, so it is never asked. */
  masked: boolean;
  fetchView?: FetchWorkReplay;
  load?: (view: WorkReplayView, deps: LoadDeps) => Promise<LoadedReplay>;
  now?: () => number;
}

export function useWorkReplay({
  jobId,
  candidateId,
  masked,
  fetchView = fetchWorkReplay,
  load = loadReplay,
  now = Date.now,
}: UseWorkReplayOptions) {
  const [summary, setSummary] = useState<ReplaySummary>(
    masked ? { status: 'masked' } : { status: 'loading' },
  );
  const [playback, setPlayback] = useState<ReplayPlayback>({ status: 'idle' });
  // The functions are read through a ref, so a caller passing new ones on each render never asks again.
  const deps = useRef({ fetchView, load, now });
  deps.current = { fetchView, load, now };
  const fetchedAt = useRef(0);
  const assetRef = useRef<string | undefined>(undefined);
  // Every request carries the generation it started in; an answer from an older one (another candidate, a newer
  // choice) is dropped.
  const generation = useRef(0);

  const ask = useCallback(
    async (assetId?: string) => {
      const mine = ++generation.current;
      setPlayback({ status: 'idle' });
      if (masked) {
        setSummary({ status: 'masked' });
        return;
      }
      setSummary({ status: 'loading' });
      try {
        const view = await deps.current.fetchView(jobId, candidateId, assetId);
        if (mine !== generation.current) return;
        fetchedAt.current = deps.current.now();
        assetRef.current = view.assetId;
        setSummary({ status: 'ready', view });
      } catch (error) {
        if (mine !== generation.current) return;
        const kind =
          error instanceof WorkReplayError ? error.kind : 'unavailable';
        setSummary({ status: kind });
      }
    },
    [jobId, candidateId, masked],
  );

  useEffect(() => {
    assetRef.current = undefined;
    void ask();
    return () => {
      generation.current += 1;
    };
  }, [ask]);

  const watch = useCallback(
    async (startAt: StartAt = 'start', forceFresh = false) => {
      if (summary.status !== 'ready') return;
      const mine = ++generation.current;
      setPlayback({ status: 'loading', done: 0, total: 0, startAt });
      try {
        let view = summary.view;
        const { fetchView: getView, load: read, now: clock } = deps.current;
        if (forceFresh || clock() - fetchedAt.current > LINK_FRESH_MS) {
          view = await getView(jobId, candidateId, view.assetId);
          if (mine !== generation.current) return;
          fetchedAt.current = clock();
          setSummary({ status: 'ready', view });
        }
        const loaded = await read(view, {
          onProgress: (done, total) => {
            if (mine === generation.current)
              setPlayback({ status: 'loading', done, total, startAt });
          },
        });
        if (mine !== generation.current) return;
        setPlayback({ status: 'ready', loaded, startAt });
      } catch {
        if (mine === generation.current) setPlayback({ status: 'failed' });
      }
    },
    [summary, jobId, candidateId],
  );

  /** Another replay this job holds for the candidate. */
  const choose = useCallback((assetId: string) => void ask(assetId), [ask]);

  const retry = useCallback(() => void ask(assetRef.current), [ask]);

  return { summary, playback, watch, choose, retry };
}
